import { tx } from "@/i18n/tx";
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { AppShell, Loader } from "@/components/AppShell";
import { Check, ArrowLeft, Sparkles } from "lucide-react";

export default function Abbonamenti() {
  const [plans, setPlans] = useState(null);
  const [trialDays, setTrialDays] = useState(7);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/plans").then((r) => { setPlans(r.data.plans); setTrialDays(r.data.trial_days); });
  }, []);

  if (!plans) return <Loader />;

  const benefits = [
    tx("Una domanda e una lettura personalizzata ogni giorno"),
    tx("Simboli e archetipi evocativi"),
    tx("Diario privato con storico e ricerca"),
    tx("Sintesi settimanale del tuo percorso"),
    tx("Esportazione del diario in PDF"),
  ];

  return (
    <AppShell nav={false}>
      <div className="anima-backdrop min-h-screen">
        <div className="px-7 pt-10 pb-14">
          <button onClick={() => navigate(-1)} className="text-anima-muted mb-8 flex items-center gap-1 text-sm" data-testid="pricing-back">
            <ArrowLeft size={16} />{tx("Indietro")}</button>

          <h1 className="font-serif text-4xl text-anima-ink mb-2">{tx("Continua il tuo ascolto")}</h1>
          <p className="text-anima-muted mb-6">{tx("Prova gratuita di")} {trialDays} {tx("giorni. Alla fine non viene addebitato nulla: l'abbonamento parte solo se scegli un piano.")}</p>

          <div className="anima-card rounded-3xl p-5 mb-8 space-y-2.5">
            {benefits.map((b) => (
              <div key={b} className="flex items-start gap-2.5">
                <Check size={16} className="text-anima-sagedeep mt-0.5 shrink-0" />
                <span className="text-sm text-anima-ink">{b}</span>
              </div>
            ))}
          </div>

          <div className="space-y-4">
            {plans.map((p, i) => (
              <motion.div
                key={p.lookup_key} data-testid={`plan-${p.lookup_key}`}
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1, duration: 0.5 }}
                className={`rounded-3xl p-6 border ${p.promo ? "bg-anima-gold/15 border-anima-gold/40" : "bg-white/60 border-anima-ink/8"}`}
              >
                <div className="flex items-start justify-between mb-1">
                  <div>
                    <p className="font-serif text-2xl text-anima-ink flex items-center gap-2">
                      {tx(p.name)}
                      {p.promo && <span className="text-[10px] uppercase tracking-wider bg-anima-golddeep text-anima-ivory px-2 py-0.5 rounded-full">{tx("Offerta di lancio")}</span>}
                    </p>
                    <p className="text-sm text-anima-muted mt-0.5">{tx(p.label)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-serif text-3xl text-anima-ink whitespace-nowrap flex items-baseline gap-0.5 justify-end">
                      {p.price_display}<span className="text-base">&euro;</span>
                    </p>
                    {p.ordinary_display && (
                      <p className="text-xs text-anima-muted line-through">{p.ordinary_display} {tx("€/anno")}</p>
                    )}
                  </div>
                </div>
                {p.period_label && <p className="text-xs text-anima-muted -mt-0.5">{tx(p.period_label)}</p>}
                {p.note && (
                  <p className="text-xs text-anima-sagedeep mt-2 leading-relaxed" data-testid={`plan-note-${p.lookup_key}`}>{tx(p.note)}</p>
                )}
                <button
                  data-testid={`choose-${p.lookup_key}`} onClick={() => navigate(`/checkout/${p.lookup_key}`)}
                  className={`mt-3 w-full rounded-full py-3.5 font-medium active:scale-95 transition-transform duration-200 flex items-center justify-center gap-2 ${p.promo ? "bg-anima-golddeep text-anima-ivory" : "bg-anima-sagedeep text-anima-ivory"}`}
                >
                  <Sparkles size={16} />{tx("Scegli questo piano")}</button>
              </motion.div>
            ))}
          </div>

          <div className="anima-card rounded-2xl p-4 mt-6 text-xs text-anima-muted leading-relaxed">
            <p className="text-anima-ink text-sm font-medium mb-1.5">{tx("Prima di procedere")}</p>
            <ul className="space-y-1">
              <li>{tx("· Hai")} {trialDays} {tx("giorni di prova gratuita: alla fine non viene addebitato nulla, l'abbonamento parte solo se scegli un piano.")}</li>
              <li>{tx("· Mensile 9,90 €/mese. Annuale in promozione: 70 € il primo anno, poi rinnovo automatico a 79 €/anno.")}</li>
              <li>{tx("· Prima del pagamento vedrai un riepilogo con importo di oggi e data del prossimo rinnovo.")}</li>
              <li>{tx("· Puoi disattivare il rinnovo in qualsiasi momento dal tuo Profilo, senza penali.")}</li>
              <li>{tx("· Pagamenti sicuri gestiti da Stripe: i dati della carta non passano mai dai nostri server.")}</li>
            </ul>
          </div>

          <p className="text-[11px] text-anima-muted text-center mt-6 leading-relaxed">{tx("Prezzi in Euro, IVA dove applicabile. Rinnovo automatico salvo disdetta.")}<br />{tx("Procedendo accetti i")}{" "}
            <Link to="/terms" className="underline text-anima-sagedeep">{tx("Termini di utilizzo")}</Link>{" "}
            e la{" "}
            <Link to="/privacy" className="underline text-anima-sagedeep">{tx("Privacy Policy")}</Link>.
          </p>
        </div>
      </div>
    </AppShell>
  );
}
